import React, { useState, useEffect } from 'react';
import { formatPrice } from '../../services/indiaAPI';
import { THEME } from '../../styles/theme';

// Storage key
const KEY = 'jarvis_india_trades';

function loadTrades() {
  try { return JSON.parse(localStorage.getItem(KEY) || '[]'); } catch { return []; }
}
function saveTrades(trades) {
  localStorage.setItem(KEY, JSON.stringify(trades));
}

const EMPTY = {
  symbol: '', name: '', type: 'EQUITY',  // EQUITY | FNO_STOCK | FNO_INDEX
  direction: 'BUY', entry: '', exit: '', qty: '', lotSize: '',
  sl: '', tp: '', brokerage: '40',       // Zerodha flat ₹20 each side = ₹40
  date: new Date().toISOString().split('T')[0],
  session: 'NSE', notes: '', grade: 'B',
  result: 'OPEN',
};

const GRADES = ['A+','A','B','C','D'];
const SESSIONS = ['NSE','BSE','F&O'];

const S = {
  wrap:     { padding: '20px', maxWidth: '1100px', margin: '0 auto' },
  title:    { fontSize: '22px', fontWeight: '800', color: THEME.colors.blue, fontFamily: THEME.fonts.hud, letterSpacing: '2px', marginBottom: '20px' },
  tabs:     { display: 'flex', gap: '8px', marginBottom: '24px' },
  tab:      (a) => ({ padding: '9px 20px', borderRadius: '8px', border: `1px solid ${a ? THEME.colors.blue : 'rgba(255,255,255,0.1)'}`, background: a ? 'rgba(0,212,255,0.1)' : 'transparent', color: a ? THEME.colors.blue : '#9ca3af', cursor: 'pointer', fontWeight: '700', fontSize: '13px' }),

  form:     { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '12px', padding: '20px', marginBottom: '20px' },
  grid2:    { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' },
  grid3:    { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '14px', marginBottom: '14px' },
  grid4:    { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '14px', marginBottom: '14px' },
  label:    { fontSize: '11px', color: 'rgba(255,255,255,0.4)', letterSpacing: '1px', marginBottom: '5px' },
  input:    { width: '100%', padding: '10px 12px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: '#fff', fontSize: '14px', outline: 'none', boxSizing: 'border-box', fontFamily: THEME.fonts.body },
  select:   { width: '100%', padding: '10px 12px', background: '#0f172a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: '#fff', fontSize: '14px', outline: 'none', boxSizing: 'border-box' },
  btn:      { padding: '11px 24px', borderRadius: '8px', border: 'none', background: THEME.colors.blue, color: '#000', cursor: 'pointer', fontWeight: '700', fontSize: '14px', marginRight: '10px' },
  btnDel:   { padding: '7px 14px', borderRadius: '6px', border: '1px solid #ef444433', background: 'transparent', color: '#ef4444', cursor: 'pointer', fontSize: '12px' },

  pnlBox:   (pnl) => ({ background: pnl > 0 ? '#10b98111' : pnl < 0 ? '#ef444411' : 'rgba(255,255,255,0.03)', border: `1px solid ${pnl > 0 ? '#10b98133' : pnl < 0 ? '#ef444433' : 'rgba(255,255,255,0.07)'}`, borderRadius: '8px', padding: '14px', marginBottom: '14px' }),
  pnlLabel: { fontSize: '11px', color: 'rgba(255,255,255,0.4)', letterSpacing: '1px', marginBottom: '4px' },
  pnlVal:   (pnl) => ({ fontSize: '22px', fontWeight: '800', color: pnl > 0 ? '#10b981' : pnl < 0 ? '#ef4444' : '#9ca3af', fontFamily: THEME.fonts.hud }),

  statsRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '12px', marginBottom: '20px' },
  statCard: { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '8px', padding: '12px' },
  statLbl:  { fontSize: '10px', color: 'rgba(255,255,255,0.35)', letterSpacing: '1px' },
  statVal:  (c='#fff') => ({ fontSize: '18px', fontWeight: '700', color: c, fontFamily: THEME.fonts.hud }),

  tradeRow: { background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '8px', padding: '14px 16px', marginBottom: '8px', display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr auto', gap: '12px', alignItems: 'center' },
  badge:    (c) => ({ padding: '3px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: '700', background: c+'22', color: c, border: `1px solid ${c}33` }),
};

// P&L calculation (equity vs F&O)
function calcPnL(trade) {
  const entry = parseFloat(trade.entry);
  const exit  = parseFloat(trade.exit);
  const qty   = parseFloat(trade.qty);
  const brok  = parseFloat(trade.brokerage || 0);
  if (!entry || !exit || !qty) return null;

  let gross;
  if (trade.type === 'EQUITY') {
    gross = (exit - entry) * qty * (trade.direction === 'BUY' ? 1 : -1);
  } else {
    // F&O: qty = number of lots, price move is per share × lot size
    const lot = parseFloat(trade.lotSize) || 1;
    gross = (exit - entry) * qty * lot * (trade.direction === 'BUY' ? 1 : -1);
  }
  return gross - brok;
}

function calcRR(trade) {
  const entry = parseFloat(trade.entry);
  const sl    = parseFloat(trade.sl);
  const tp    = parseFloat(trade.tp);
  if (!entry || !sl || !tp) return null;
  const risk   = Math.abs(entry - sl);
  const reward = Math.abs(tp - entry);
  if (!risk) return null;
  return (reward / risk).toFixed(1);
}

export default function IndiaJournal() {
  const [tab,    setTab]    = useState('log');
  const [trades, setTrades] = useState(loadTrades);
  const [form,   setForm]   = useState(EMPTY);
  const [edit,   setEdit]   = useState(null);

  useEffect(() => { saveTrades(trades); }, [trades]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = () => {
    const pnl = form.result !== 'OPEN' ? calcPnL(form) : null;
    const trade = {
      ...form,
      id:  edit || ('IT' + Date.now()),
      pnl: pnl,
      rr:  calcRR(form),
    };
    if (edit) {
      setTrades(ts => ts.map(t => t.id === edit ? trade : t));
      setEdit(null);
    } else {
      setTrades(ts => [trade, ...ts]);
    }
    setForm(EMPTY);
  };

  const deleteTrade = (id) => setTrades(ts => ts.filter(t => t.id !== id));

  const startEdit = (trade) => {
    setForm(trade);
    setEdit(trade.id);
    setTab('log');
  };

  // Stats
  const closed   = trades.filter(t => t.result !== 'OPEN');
  const open     = trades.filter(t => t.result === 'OPEN');
  const totalPnL = closed.reduce((s, t) => s + (t.pnl || 0), 0);
  const wins     = closed.filter(t => (t.pnl || 0) > 0);
  const winRate  = closed.length ? Math.round(wins.length / closed.length * 100) : 0;
  const today    = new Date().toISOString().split('T')[0];
  const todayPnL = closed.filter(t => t.date === today).reduce((s, t) => s + (t.pnl || 0), 0);

  const previewPnL = calcPnL(form);

  return (
    <div style={S.wrap}>
      <div style={S.title}>INDIA TRADE JOURNAL</div>

      <div style={S.tabs}>
        {['log','history','stats'].map(t => (
          <button key={t} style={S.tab(tab===t)} onClick={() => setTab(t)}>
            {t === 'log' ? 'Log Trade' : t === 'history' ? 'History' : 'Stats'}
          </button>
        ))}
      </div>

      {/* LOG TRADE */}
      {tab === 'log' && (
        <div style={S.form}>
          <div style={{ fontSize: '13px', fontWeight: '700', color: THEME.colors.blue, marginBottom: '16px' }}>
            {edit ? 'Edit Trade' : 'New Indian Stock/F&O Trade'}
          </div>

          <div style={S.grid3}>
            <div>
              <div style={S.label}>STOCK SYMBOL</div>
              <input style={S.input} placeholder="RELIANCE, TCS, NIFTY..." value={form.symbol} onChange={e => set('symbol', e.target.value.toUpperCase())}/>
            </div>
            <div>
              <div style={S.label}>COMPANY NAME</div>
              <input style={S.input} placeholder="Reliance Industries" value={form.name} onChange={e => set('name', e.target.value)}/>
            </div>
            <div>
              <div style={S.label}>DATE</div>
              <input style={S.input} type="date" value={form.date} onChange={e => set('date', e.target.value)}/>
            </div>
          </div>

          <div style={S.grid4}>
            <div>
              <div style={S.label}>TYPE</div>
              <select style={S.select} value={form.type} onChange={e => set('type', e.target.value)}>
                <option value="EQUITY">Equity (Shares)</option>
                <option value="FNO_STOCK">F&O — Stock Options/Futures</option>
                <option value="FNO_INDEX">F&O — Index (Nifty/BNF)</option>
              </select>
            </div>
            <div>
              <div style={S.label}>DIRECTION</div>
              <select style={S.select} value={form.direction} onChange={e => set('direction', e.target.value)}>
                <option value="BUY">BUY (Long)</option>
                <option value="SELL">SELL (Short)</option>
              </select>
            </div>
            <div>
              <div style={S.label}>{form.type === 'EQUITY' ? 'QTY (SHARES)' : 'QTY (LOTS)'}</div>
              <input style={S.input} type="number" placeholder={form.type === 'EQUITY' ? '100' : '1'} value={form.qty} onChange={e => set('qty', e.target.value)}/>
            </div>
            {form.type !== 'EQUITY' && (
              <div>
                <div style={S.label}>LOT SIZE</div>
                <input style={S.input} type="number" placeholder="75 (Nifty) / 35 (BNF)" value={form.lotSize} onChange={e => set('lotSize', e.target.value)}/>
              </div>
            )}
          </div>

          <div style={S.grid4}>
            <div>
              <div style={S.label}>ENTRY PRICE (₹)</div>
              <input style={S.input} type="number" step="0.01" placeholder="1316.50" value={form.entry} onChange={e => set('entry', e.target.value)}/>
            </div>
            <div>
              <div style={S.label}>STOP LOSS (₹)</div>
              <input style={S.input} type="number" step="0.01" value={form.sl} onChange={e => set('sl', e.target.value)}/>
            </div>
            <div>
              <div style={S.label}>TARGET (₹)</div>
              <input style={S.input} type="number" step="0.01" value={form.tp} onChange={e => set('tp', e.target.value)}/>
            </div>
            <div>
              <div style={S.label}>BROKERAGE (₹)</div>
              <input style={S.input} type="number" value={form.brokerage} onChange={e => set('brokerage', e.target.value)}/>
            </div>
          </div>

          <div style={S.grid3}>
            <div>
              <div style={S.label}>RESULT</div>
              <select style={S.select} value={form.result} onChange={e => set('result', e.target.value)}>
                <option value="OPEN">Open</option>
                <option value="WIN">Win</option>
                <option value="LOSS">Loss</option>
                <option value="BREAKEVEN">Breakeven</option>
              </select>
            </div>
            {form.result !== 'OPEN' && (
              <div>
                <div style={S.label}>EXIT PRICE (₹)</div>
                <input style={S.input} type="number" step="0.01" value={form.exit} onChange={e => set('exit', e.target.value)}/>
              </div>
            )}
            <div>
              <div style={S.label}>GRADE</div>
              <select style={S.select} value={form.grade} onChange={e => set('grade', e.target.value)}>
                {GRADES.map(g => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
          </div>

          <div style={{ marginBottom: '14px' }}>
            <div style={S.label}>NOTES / SETUP REASON</div>
            <textarea style={{ ...S.input, minHeight: '60px', resize: 'vertical' }} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Why this trade? What setup? OB, FVG, breakout..."/>
          </div>

          {/* Live P&L preview */}
          {previewPnL !== null && (
            <div style={S.pnlBox(previewPnL)}>
              <div style={S.pnlLabel}>ESTIMATED P&L</div>
              <div style={S.pnlVal(previewPnL)}>
                {previewPnL >= 0 ? '+' : ''}₹{previewPnL.toFixed(2)}
              </div>
              {calcRR(form) && (
                <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)', marginTop: '4px' }}>
                  R:R = 1:{calcRR(form)}
                </div>
              )}
            </div>
          )}

          <button style={S.btn} onClick={submit}>{edit ? 'Update Trade' : 'Log Trade'}</button>
          {edit && <button style={{ ...S.btn, background: 'transparent', border: '1px solid rgba(255,255,255,0.1)', color: '#9ca3af' }} onClick={() => { setEdit(null); setForm(EMPTY); }}>Cancel</button>}
        </div>
      )}

      {/* HISTORY */}
      {tab === 'history' && (
        <div>
          {open.length > 0 && (
            <>
              <div style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '2px', color: '#f59e0b', marginBottom: '10px' }}>OPEN TRADES ({open.length})</div>
              {open.map(t => <TradeRow key={t.id} trade={t} onEdit={startEdit} onDelete={deleteTrade}/>)}
              <div style={{ marginBottom: '20px' }}/>
            </>
          )}
          <div style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '2px', color: 'rgba(255,255,255,0.3)', marginBottom: '10px' }}>CLOSED TRADES ({closed.length})</div>
          {closed.length === 0 && <div style={{ color: THEME.colors.textMuted, padding: '20px 0' }}>No closed trades yet</div>}
          {closed.map(t => <TradeRow key={t.id} trade={t} onEdit={startEdit} onDelete={deleteTrade}/>)}
        </div>
      )}

      {/* STATS */}
      {tab === 'stats' && (
        <div>
          <div style={S.statsRow}>
            {[
              { label: 'TOTAL P&L',    value: (totalPnL >= 0 ? '+₹' : '-₹') + Math.abs(totalPnL).toFixed(0), col: totalPnL > 0 ? '#10b981' : '#ef4444' },
              { label: "TODAY'S P&L",  value: (todayPnL >= 0 ? '+₹' : '-₹') + Math.abs(todayPnL).toFixed(0), col: todayPnL > 0 ? '#10b981' : todayPnL < 0 ? '#ef4444' : '#9ca3af' },
              { label: 'WIN RATE',     value: winRate + '%',    col: winRate >= 60 ? '#10b981' : winRate >= 40 ? '#f59e0b' : '#ef4444' },
              { label: 'TOTAL TRADES', value: closed.length,   col: '#fff' },
              { label: 'WINS',         value: wins.length,     col: '#10b981' },
              { label: 'LOSSES',       value: closed.length - wins.length, col: '#ef4444' },
              { label: 'OPEN',         value: open.length,     col: '#f59e0b' },
              { label: 'BEST TRADE',   value: closed.length ? '+₹' + Math.max(...closed.map(t => t.pnl||0)).toFixed(0) : '—', col: '#10b981' },
            ].map(item => (
              <div key={item.label} style={S.statCard}>
                <div style={S.statLbl}>{item.label}</div>
                <div style={S.statVal(item.col)}>{item.value}</div>
              </div>
            ))}
          </div>

          {/* Sector breakdown */}
          <SectorBreakdown trades={closed}/>
        </div>
      )}
    </div>
  );
}

function TradeRow({ trade, onEdit, onDelete }) {
  const pnl = trade.pnl;
  const isOpen = trade.result === 'OPEN';
  const typeColor = trade.type === 'EQUITY' ? '#00d4ff' : trade.type === 'FNO_INDEX' ? '#7c3aed' : '#f59e0b';
  const S2 = {
    wrap: { background: 'rgba(255,255,255,0.02)', border: `1px solid ${isOpen ? 'rgba(245,158,11,0.2)' : 'rgba(255,255,255,0.06)'}`, borderRadius: '8px', padding: '12px 16px', marginBottom: '8px' },
    row:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' },
    name: { fontSize: '14px', fontWeight: '700', color: '#e2e8f0' },
    meta: { fontSize: '11px', color: 'rgba(255,255,255,0.35)', marginTop: '2px' },
    pnl:  { fontSize: '16px', fontWeight: '800', color: pnl > 0 ? '#10b981' : pnl < 0 ? '#ef4444' : '#9ca3af', fontFamily: THEME.fonts.hud },
    actions: { display: 'flex', gap: '8px' },
    editBtn: { padding: '5px 12px', borderRadius: '5px', border: '1px solid rgba(255,255,255,0.1)', background: 'transparent', color: '#9ca3af', cursor: 'pointer', fontSize: '11px' },
    delBtn:  { padding: '5px 10px', borderRadius: '5px', border: '1px solid #ef444433', background: 'transparent', color: '#ef4444', cursor: 'pointer', fontSize: '11px' },
    badge: (c) => ({ display: 'inline-block', padding: '2px 8px', borderRadius: '10px', fontSize: '9px', fontWeight: '700', background: c+'22', color: c, border: `1px solid ${c}33`, marginRight: '4px' }),
  };
  return (
    <div style={S2.wrap}>
      <div style={S2.row}>
        <div>
          <div style={S2.name}>
            {trade.symbol}
            <span style={S2.badge(typeColor)}>{trade.type.replace('_',' ')}</span>
            <span style={S2.badge(trade.direction === 'BUY' ? '#10b981' : '#ef4444')}>{trade.direction}</span>
            {isOpen && <span style={S2.badge('#f59e0b')}>OPEN</span>}
          </div>
          <div style={S2.meta}>
            {trade.date} · Entry ₹{trade.entry} · {trade.type !== 'EQUITY' ? `${trade.qty} lots × ${trade.lotSize}` : `${trade.qty} shares`}
            {trade.rr && ` · R:R 1:${trade.rr}`}
            {trade.grade && ` · Grade ${trade.grade}`}
            {trade.notes && ` · ${trade.notes.substring(0, 50)}`}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          {!isOpen && (
            <div style={S2.pnl}>
              {pnl != null ? `${pnl >= 0 ? '+' : ''}₹${pnl.toFixed(0)}` : '—'}
            </div>
          )}
          <div style={S2.actions}>
            <button style={S2.editBtn} onClick={() => onEdit(trade)}>Edit</button>
            <button style={S2.delBtn}  onClick={() => onDelete(trade.id)}>✕</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SectorBreakdown({ trades }) {
  const map = {};
  trades.forEach(t => {
    const key = t.symbol || 'Other';
    if (!map[key]) map[key] = { trades: 0, pnl: 0 };
    map[key].trades++;
    map[key].pnl += t.pnl || 0;
  });
  const rows = Object.entries(map)
    .map(([sym, v]) => ({ sym, ...v }))
    .sort((a, b) => b.pnl - a.pnl)
    .slice(0, 10);

  if (!rows.length) return null;

  return (
    <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '10px', padding: '16px' }}>
      <div style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '2px', color: 'rgba(255,255,255,0.3)', marginBottom: '14px' }}>P&L BY STOCK</div>
      {rows.map(r => (
        <div key={r.sym} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
          <div style={{ fontSize: '13px', color: '#e2e8f0', fontWeight: '600' }}>{r.sym}</div>
          <div style={{ display: 'flex', gap: '20px', fontSize: '12px' }}>
            <span style={{ color: 'rgba(255,255,255,0.4)' }}>{r.trades} trade{r.trades > 1 ? 's' : ''}</span>
            <span style={{ fontWeight: '700', color: r.pnl >= 0 ? '#10b981' : '#ef4444' }}>
              {r.pnl >= 0 ? '+' : ''}₹{r.pnl.toFixed(0)}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}
